import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, ExternalLink, Loader2 } from 'lucide-react';
import { Bar, BarChart, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import type { Card, MetricDef, MetricResult, Tone } from '@/api/boards';
import { Markdown } from '@/components/Markdown';
import { drillFor, fmtAxisDay, fmtCell, fmtMetric, levelFor, type Level } from '@/lib/boards';
import { cn } from '@/lib/utils';

/**
 * One dashboard card. Sizes follow the grid cell (CSS container queries), so the same card reads on
 * a laptop and on a wall display. Colour rules (dataviz skill): status breakdowns use the theme's
 * status tokens beside their labels; plain categories are one hue; trends use the validated
 * --chart-series-1/2 pair with a legend; KPI colour only when the card has thresholds.
 */

const TONE_COLOR: Record<Tone, string> = {
  serious: 'hsl(var(--destructive) / 0.55)',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  destructive: 'hsl(var(--destructive))',
  info: 'hsl(var(--info))',
  neutral: 'hsl(var(--neutral))',
  subtle: 'hsl(var(--neutral) / 0.45)',
};
const SERIES = ['var(--chart-series-1)', 'var(--chart-series-2)', 'hsl(var(--neutral))'];
const AXIS = '#94a3b8';
const GRID = 'rgba(148,163,184,0.18)';

const LEVEL_TEXT: Record<NonNullable<Level>, string> = { ok: 'text-success', warn: 'text-warning', bad: 'text-destructive' };
const LEVEL_BAR: Record<NonNullable<Level>, string> = { ok: 'bg-success', warn: 'bg-warning', bad: 'bg-destructive' };

export interface CardScopeNote { label: string; muted?: boolean }

export function BoardCard({ card, def, result, scopeNote, editing, display, onOpenSettings }: {
  card: Card;
  def: MetricDef | undefined;
  result: MetricResult | undefined;
  /** "LOC Property Group" when the metric is client-filtered, "All clients" when it can't be. */
  scopeNote?: CardScopeNote | null;
  editing?: boolean;
  display?: boolean;
  onOpenSettings?: () => void;
}) {
  const title = card.title?.trim() || def?.label || (card.type === 'note' ? '' : card.type === 'links' ? 'Links' : card.type === 'clock' ? '' : 'Card');
  const drill = !editing && !display ? drillFor(card.metric) : null;
  const metricCard = !['note', 'links', 'clock'].includes(card.type);

  return (
    <div className={cn('group/card flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm', editing && 'ring-1 ring-transparent hover:ring-ring/40')}
      style={{ containerType: 'size' }}>
      {(title || editing) && (
        <div className={cn('flex shrink-0 items-center gap-2 px-3 pt-2', editing && 'board-drag-handle cursor-move')}>
          <h3 className={cn('min-w-0 flex-1 truncate font-medium text-muted-foreground', display ? 'text-[clamp(0.8rem,1.1vw,1.15rem)]' : 'text-[12.5px]')} title={def?.description}>{title}</h3>
          {scopeNote && <span className={cn('shrink-0 truncate rounded px-1.5 text-[10px]', scopeNote.muted ? 'bg-muted text-muted-foreground' : 'bg-brand-soft text-brand')} title={scopeNote.muted ? "This metric can't be filtered by client" : 'Filtered to this client'}>{scopeNote.label}</span>}
          {drill && <Link to={drill} className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/card:opacity-100" title="Open the page behind this number"><ArrowUpRight className="h-3.5 w-3.5" /></Link>}
          {editing && onOpenSettings && (
            <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={onOpenSettings}
              className="shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">Edit</button>
          )}
        </div>
      )}
      {/* Absolutely filled so charts' 100% height resolves to the card's real size. */}
      <div className="relative min-h-0 flex-1"><div className="absolute inset-0 px-3 pb-2 pt-1">
        {metricCard && !card.metric ? <Center>Pick a metric</Center>
          : metricCard && !def ? <Center>This metric no longer exists</Center>
          : metricCard && (!result || result.loading) ? <Center><Loader2 className="h-4 w-4 animate-spin" />{result?.loading && <span className="ml-1.5">Loading…</span>}</Center>
          : metricCard && result?.error ? <Center tone="bad"><AlertTriangle className="mr-1 h-3.5 w-3.5" />{result.error}</Center>
          : <Body card={card} def={def} result={result} display={display} />}
      </div></div>
    </div>
  );
}

function Center({ children, tone }: { children: React.ReactNode; tone?: 'bad' }) {
  return <div className={cn('flex h-full items-center justify-center text-center text-xs', tone === 'bad' ? 'text-destructive' : 'text-muted-foreground')}>{children}</div>;
}

function Body({ card, def, result, display }: { card: Card; def: MetricDef | undefined; result: MetricResult | undefined; display?: boolean }) {
  const v = result?.value;
  switch (card.type) {
    case 'clock': return <Clock display={display} />;
    case 'note': return <div className="h-full overflow-auto text-sm">{card.options?.text ? <Markdown content={card.options.text} /> : <Center>Empty note</Center>}</div>;
    case 'links': return <Links links={card.options?.links ?? []} />;
  }
  if (!v) return <Center>No data</Center>;

  if (card.type === 'kpi' && v.kind === 'number') {
    const level = levelFor(v.value, card, def);
    const showTotal = card.options?.showTotal ?? v.total != null;
    const share = v.total ? Math.min(100, ((v.value ?? 0) / v.total) * 100) : null;
    return (
      <div className="flex h-full flex-col justify-center">
        <div className={cn('font-semibold leading-none tabular-nums', level ? LEVEL_TEXT[level] : 'text-foreground')} style={{ fontSize: 'clamp(1.5rem, 42cqh, 7rem)' }}>
          {fmtMetric(v.value, def?.unit)}
        </div>
        {showTotal && v.total != null && (
          <div className="mt-1.5">
            <div className="text-muted-foreground" style={{ fontSize: 'clamp(0.7rem, 11cqh, 1.4rem)' }}>of {fmtMetric(v.total, def?.unit)} · {Math.round(share ?? 0)}%</div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted"><div className={cn('h-full rounded-full', level ? LEVEL_BAR[level] : 'bg-brand')} style={{ width: `${share ?? 0}%` }} /></div>
          </div>
        )}
      </div>
    );
  }

  if ((card.type === 'bar' || card.type === 'donut') && v.kind === 'breakdown') {
    const items = v.items.filter((i) => i.value > 0).slice(0, card.options?.limit ?? 12);
    if (!items.length) return <Center>Nothing to show</Center>;
    const toned = items.some((i) => i.tone);
    const color = (i: { tone?: Tone }) => (toned ? TONE_COLOR[i.tone ?? 'neutral'] : 'var(--chart-series-1)');
    const fmt = (n: number) => fmtMetric(n, def?.unit);
    const tick = { fontSize: display ? 13 : 11, fill: AXIS };
    if (card.type === 'donut') {
      const total = items.reduce((n, i) => n + i.value, 0);
      // Donuts with plain categories still need distinct slices: neutral steps of the one hue.
      const slice = (i: (typeof items)[number], idx: number) => (toned ? color(i) : `color-mix(in oklab, var(--chart-series-1) ${100 - idx * (60 / Math.max(1, items.length - 1))}%, transparent)`);
      return (
        <div className="flex h-full items-center gap-3">
          <div className="h-full min-w-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={items} dataKey="value" nameKey="label" innerRadius="58%" outerRadius="92%" stroke="hsl(var(--card))" strokeWidth={2} isAnimationActive={false}>
                  {items.map((i, idx) => <Cell key={i.label} fill={slice(i, idx)} />)}
                </Pie>
                <Tooltip content={<Tip fmt={fmt} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="max-h-full min-w-0 max-w-[55%] space-y-1 overflow-auto" style={{ fontSize: display ? 'clamp(0.75rem, 1vw, 1.1rem)' : 12 }}>
            {items.map((i, idx) => (
              <li key={i.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: slice(i, idx) }} />
                <span className="truncate text-muted-foreground">{i.label}</span>
                <span className="ml-auto pl-2 font-medium tabular-nums text-foreground">{fmt(i.value)}</span>
                <span className="w-9 text-right tabular-nums text-muted-foreground">{Math.round((i.value / total) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={items} layout="vertical" margin={{ top: 4, right: 36, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          {/* Wide tick so recharts never wraps a label over two lines; the formatter keeps it inside the axis. */}
          <YAxis type="category" dataKey="label" width={display ? 160 : 118} tick={{ ...tick, width: 1000 }} tickLine={false} axisLine={false}
            tickFormatter={(s: string) => (s.length > 17 ? `${s.slice(0, 16)}…` : s)} />
          <Tooltip content={<Tip fmt={fmt} />} cursor={{ fill: 'hsl(var(--muted) / 0.5)' }} />
          <Bar dataKey="value" name={def?.label ?? 'Value'} radius={[0, 3, 3, 0]} isAnimationActive={false}
            label={{ position: 'right', fill: AXIS, fontSize: display ? 13 : 11, formatter: (n: unknown) => fmt(Number(n)) }}>
            {items.map((i) => <Cell key={i.label} fill={color(i)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (card.type === 'line' && v.kind === 'series') {
    const series = def?.series ?? [];
    if (v.points.length < 2) return <Center>Collecting history: trends need two days of snapshots.</Center>;
    const pctOnly = series.every((s) => s.unit === 'percent');
    const fmt = (n: number) => (pctOnly ? `${n}%` : fmtMetric(n));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={v.points} margin={{ top: 6, right: 10, bottom: 0, left: -8 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="x" tickFormatter={fmtAxisDay} tick={{ fontSize: display ? 13 : 11, fill: AXIS }} tickLine={false} axisLine={false} minTickGap={24} />
          <YAxis tick={{ fontSize: display ? 13 : 11, fill: AXIS }} tickLine={false} axisLine={false} width={40} domain={pctOnly ? [0, 100] : ['auto', 'auto']} tickFormatter={fmt} />
          <Tooltip content={<Tip fmt={fmt} labelFmt={fmtAxisDay} />} />
          {series.length > 1 && <Legend iconType="plainline" wrapperStyle={{ fontSize: display ? 13 : 11 }} />}
          {series.map((s, i) => <Line key={s.key} dataKey={s.key} name={s.label} stroke={SERIES[i % SERIES.length]} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />)}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (card.type === 'list' && v.kind === 'table') {
    const rows = v.rows.slice(0, card.options?.limit ?? 10);
    if (!rows.length) return <Center>Nothing to list</Center>;
    return (
      <ul className="h-full divide-y divide-divider overflow-auto" style={{ fontSize: display ? 'clamp(0.8rem, 1.05vw, 1.2rem)' : 13 }}>
        {rows.map((r, i) => (
          <li key={`${r.label}${i}`} className="flex items-baseline gap-2 py-1">
            <div className="min-w-0 flex-1">
              <div className="truncate text-foreground">{r.label}</div>
              {r.sub && <div className="truncate text-[0.8em] text-muted-foreground">{r.sub}</div>}
            </div>
            <span className="shrink-0 font-medium tabular-nums text-foreground">{fmtCell(r.value, def?.unit)}</span>
          </li>
        ))}
        {v.rows.length > rows.length && <li className="py-1 text-[0.8em] text-muted-foreground">+ {v.rows.length - rows.length} more</li>}
      </ul>
    );
  }

  return <Center>This card type can't show this metric</Center>;
}

function Tip({ active, payload, label, fmt, labelFmt }: { active?: boolean; payload?: { name: string; value: number; color?: string; payload?: { fill?: string } }[]; label?: string; fmt: (n: number) => string; labelFmt?: (s: string) => string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-md">
      {label != null && <div className="mb-0.5 font-medium text-foreground">{labelFmt ? labelFmt(String(label)) : label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color ?? p.payload?.fill }} />
          {p.name}: <span className="font-medium tabular-nums text-foreground">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function Clock({ display }: { display?: boolean }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000 * (display ? 1 : 15)); return () => clearInterval(t); }, [display]);
  return (
    <div className="flex h-full flex-col justify-center">
      {/* Sized by width as well as height, so a narrow card doesn't wrap "10:06 am" and push the date out. */}
      <div className="whitespace-nowrap font-semibold leading-none tabular-nums text-foreground" style={{ fontSize: 'clamp(1.25rem, min(45cqh, 18cqw), 7rem)' }}>
        {now.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
      </div>
      <div className="mt-1 truncate text-muted-foreground" style={{ fontSize: 'clamp(0.7rem, min(13cqh, 6.5cqw), 1.6rem)' }}>{now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
    </div>
  );
}

function Links({ links }: { links: { label: string; url: string }[] }) {
  if (!links.length) return <Center>No links yet</Center>;
  return (
    <ul className="h-full space-y-1 overflow-auto text-sm">
      {links.map((l, i) => (
        <li key={i}>
          {l.url.startsWith('/')
            ? <Link to={l.url} className="flex items-center gap-1.5 rounded px-1 py-0.5 text-foreground hover:bg-muted">{l.label}<ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link>
            : <a href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded px-1 py-0.5 text-foreground hover:bg-muted">{l.label}<ExternalLink className="h-3 w-3 text-muted-foreground" /></a>}
        </li>
      ))}
    </ul>
  );
}
